import React, { useCallback, useState, useRef } from 'react';
import {
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  View,
  Text,
  TouchableOpacity,
} from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { ActionButtons, FormInput, FormSection } from '@/components';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { ADMIN_COLORS } from '@/constants/admin';
import { useEditConversationMutation } from '@/store/api/admin/messagesApi';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RecipientSelector } from '@/components/admin/messages/CreateMessage/components/RecipientSelector';
import { useEditMessage } from '@/components/admin/messages/CreateMessage/hooks/useEditMessage';
import { useConversation } from '@/hooks/admin/useMessages';
import { useResponsive } from '@/hooks/common/useResponsive';
import { DesktopFormPage } from '@/components/common/admin/desktop/DesktopFormPage';

type EditMessageScreenProps = StackScreenProps<AdminStackParamList, 'EditMessage'>;

export const EditMessageScreen: React.FC<EditMessageScreenProps> = ({
  navigation,
  route,
}) => {
  const { conversationId } = route.params;
  const { layoutMode } = useResponsive();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  
  const { conversation, isLoading: conversationLoading } = useConversation(conversationId);
  const [editConversation, { isLoading }] = useEditConversationMutation();
  const scrollViewRef = useRef<ScrollView>(null);

  // Custom hook for edit form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
    showDiscardAlert,
    hasUnsavedChanges,
    getNewRecipients,
  } = useEditMessage(conversation);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!selectedPropertyId || !conversation) {
      showApiError('No property or conversation selected');
      return;
    }

    // Check if form is valid before submission
    if (isLoading || !formData.subject || formData.recipients.length === 0) {
      return;
    }

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      showApiError('Please fix the form errors before submitting');
      return;
    }

    try {
      // Create FormData matching admin-web implementation
      const formDataToSend = new FormData();
      
      // Basic fields
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('sendEmail', formData.sendEmail.toString());
      formDataToSend.append('propertyId', selectedPropertyId.toString());

      // Get only new recipients (not in original list)
      const newRecipients = getNewRecipients();
      const recipientLeaseCustomers = newRecipients.flatMap((r: any) => r.leaseCustomers?.[0] || r);
      
      const assignedRecipients = recipientLeaseCustomers
        .filter((r: any) => r.account && Object.keys(r.account || {}).length > 0)
        .map((r: any) => r.id);
      
      const unassignedRecipients = recipientLeaseCustomers
        .filter((r: any) => !r.account || Object.keys(r.account || {}).length <= 0)
        .map((r: any) => r.id);

      formDataToSend.append('recipients', JSON.stringify(assignedRecipients));
      formDataToSend.append('unassignedRecipients', JSON.stringify(unassignedRecipients));

      const result = await editConversation({
        body: formDataToSend,
        conversationId: conversation.id,
      }).unwrap();
      
      if (result?.success) {
        showSuccess('Conversation updated successfully');
        navigation.goBack();
      } else {
        showApiError('Failed to update conversation');
      }
    } catch (error: any) {
      console.error('Edit conversation error:', error);
      showApiError(error?.data?.message || 'Failed to update conversation');
    }
  }, [
    selectedPropertyId,
    conversation,
    validateForm,
    formData,
    editConversation,
    showSuccess,
    showApiError,
    navigation,
    getNewRecipients,
  ]);

  // Handle back navigation with unsaved changes check
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges()) {
      showDiscardAlert(() => navigation.goBack());
    } else {
      navigation.goBack();
    }
  }, [hasUnsavedChanges, showDiscardAlert, navigation]);

  // Show loading state while conversation is loading
  if (conversationLoading || !conversation) {
    return (
      <AdminErrorBoundary context="EditMessageScreen">
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading conversation...</Text>
        </View>
      </AdminErrorBoundary>
    );
  }

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="EditMessageScreenDesktop">
        <DesktopFormPage
          title="Edit Conversation"
          onSubmit={handleSubmit}
          onCancel={handleBack}
          isSubmitting={isLoading}
          submitText="Update"
          contentMaxWidth={720}
        >
          <View style={styles.desktopSection}>
            <Text style={styles.desktopSectionTitle}>Conversation Details</Text>
            <FormInput
              label="Subject"
              value={formData.subject}
              onChangeText={(value) => handleFieldChange('subject', value)}
              placeholder="Enter conversation subject"
              error={errors.subject}
              required
              maxLength={100}
            />
          </View>

          <View style={styles.desktopSection}>
            <Text style={styles.desktopSectionTitle}>Recipients</Text>
            <RecipientSelector
              recipients={formData.recipients}
              onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
              error={errors.recipients}
              messageType="groupChat"
              isEditMode={true}
              originalRecipients={formData.originalRecipients}
            />
          </View>

          <View style={styles.desktopSection}>
            <Text style={styles.desktopSectionTitle}>Notification Settings</Text>
            <TouchableOpacity
              style={styles.toggleRow}
              onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
            >
              <View style={styles.toggleContent}>
                <Text style={styles.toggleLabel}>Send email notification</Text>
                <Text style={styles.toggleSubtext}>
                  New recipients will receive an email notification about this conversation
                </Text>
              </View>
              <View style={[
                styles.toggle,
                formData.sendEmail && styles.toggleActive
              ]}>
                {formData.sendEmail && (
                  <SVG_COMPONENTS.CHECK_CIRCLE
                    width={16}
                    height={16}
                    color="#FFFFFF"
                  />
                )}
              </View>
            </TouchableOpacity>
          </View>

          <View style={[styles.infoSection, { marginTop: 0 }]}>
            <View style={styles.infoRow}>
              <SVG_COMPONENTS.X_CIRCLE
                width={16}
                height={16}
                color={ADMIN_COLORS.TEXT_SECONDARY}
              />
              <Text style={styles.infoText}>
                You can only add new recipients to this conversation. Existing recipients cannot be removed.
              </Text>
            </View>
          </View>
        </DesktopFormPage>
      </AdminErrorBoundary>
    );
  }

  // Mobile layout
  return (
    <AdminErrorBoundary context="EditMessageScreen">
      <View style={styles.container}>
        {/* Custom Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={24}
              height={24}
              color={ADMIN_COLORS.TEXT.PRIMARY}
            />
          </TouchableOpacity>
          
          <Text style={styles.headerTitle}>Edit Conversation</Text>
          
          <View style={styles.headerSpacer} />
        </View>

        {/* Main Content */}
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 100 : 0}
        >
          <ScrollView
            ref={scrollViewRef}
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Subject Section */}
            <FormSection title="Conversation Details">
              <FormInput
                label="Subject"
                value={formData.subject}
                onChangeText={(value) => handleFieldChange('subject', value)}
                placeholder="Enter conversation subject"
                error={errors.subject}
                required
                maxLength={100}
              />
            </FormSection>

            {/* Recipients Section */}
            <RecipientSelector
              recipients={formData.recipients}
              onRecipientsChange={(recipients) => handleFieldChange('recipients', recipients)}
              error={errors.recipients}
              messageType="groupChat" // Edit mode always uses group chat
              isEditMode={true}
              originalRecipients={formData.originalRecipients}
            />

            {/* Email Notification Toggle */}
            <FormSection title="Notification Settings">
              <TouchableOpacity
                style={styles.toggleRow}
                onPress={() => handleFieldChange('sendEmail', !formData.sendEmail)}
              >
                <View style={styles.toggleContent}>
                  <Text style={styles.toggleLabel}>Send email notification</Text>
                  <Text style={styles.toggleSubtext}>
                    New recipients will receive an email notification about this conversation
                  </Text>
                </View>
                <View style={[
                  styles.toggle,
                  formData.sendEmail && styles.toggleActive
                ]}>
                  {formData.sendEmail && (
                    <SVG_COMPONENTS.CHECK_CIRCLE
                      width={16}
                      height={16}
                      color="#FFFFFF"
                    />
                  )}
                </View>
              </TouchableOpacity>
            </FormSection>

            {/* Info Section */}
            <View style={styles.infoSection}>
              <View style={styles.infoRow}>
                <SVG_COMPONENTS.X_CIRCLE
                  width={16}
                  height={16}
                  color={ADMIN_COLORS.TEXT_SECONDARY}
                />
                <Text style={styles.infoText}>
                  You can only add new recipients to this conversation. Existing recipients cannot be removed.
                </Text>
              </View>
            </View>
          </ScrollView>

          {/* Action Buttons */}
          <ActionButtons
            isLoading={isLoading}
            onCancel={handleBack}
            onSubmit={handleSubmit}
            submitText="Update"
            cancelText="Cancel"
          />
        </KeyboardAvoidingView>
      </View>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    paddingBottom: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  loadingText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    paddingTop: 50, // Account for status bar
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40, // Same width as back button for centering
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 100, // Space for action buttons
  },
  desktopSection: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    padding: 16,
  },
  desktopSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
    marginTop: 8,
  },
  toggleContent: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 4,
  },
  toggleSubtext: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
  },
  toggle: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.BORDER,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: ADMIN_COLORS.PRIMARY,
    borderColor: ADMIN_COLORS.PRIMARY,
  },
  infoSection: {
    marginTop: 16,
    padding: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 12,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    lineHeight: 18,
    marginLeft: 8,
  },
});
